import { IGeneralHeader, marketData } from '@core/market-data-sdk';

interface IAssetHeader extends Omit<IGeneralHeader, 'class'> {
  class: string;
}

interface GetComparisonAssetsHeaderResponse {
  assetsHeaderList: IAssetHeader[];
  errorMessage?: string;
}

export class GetComparisonAssetsHeaderUseCase {
  async execute(
    assetsIds: string[]
  ): Promise<GetComparisonAssetsHeaderResponse> {
    let errorMessage: string;

    const assetsIdsWithNoRepeatValues = [...new Set(assetsIds)]
      .map(this.validAsset)
      .filter(Boolean);

    const assetsHeader: IAssetHeader[] = (
      await Promise.all(
        assetsIdsWithNoRepeatValues.map(async (identifier) => {
          const response = await marketData.general.getHeader(identifier, {
            safe: true,
          });

          if (!response) {
            errorMessage = `O ativo ${identifier} não foi encontrado.`;
            return null;
          }

          return {
            ...response,
            class: response?.class?.name,
            id: identifier.split(':')[0],
            assetType: identifier.split(':')[1],
            slug: response.slug ? `/${response.slug}` : null,
          };
        })
      )
    )
      .filter(Boolean)
      .map((asset, index, allHeaders) => {
        // altera a data inicial e final do ativo com gross up para corrigir o problema de calculo de periodo
        if (asset.assetType !== 'rfs-gu') return asset;

        const maxStartDate = this.maxStartDate(allHeaders);
        const minEndDate = this.minEndDate(allHeaders);

        return {
          ...asset,
          first_quote_date: maxStartDate,
          last_quote_date: minEndDate,
        };
      });

    const assetsHeaderList = assetsHeader.reduce((total, current) => {
      try {
        this.isAssetComparable(current, total);

        return [...total, current];
      } catch (error) {
        errorMessage = error.message;
        return total;
      }
    }, []);

    return {
      assetsHeaderList,
      errorMessage,
    };
  }

  private validAsset = (asset: string = '') => {
    const split = asset?.split(':');

    return split.length === 2 ? asset : null;
  };

  private hasIntersectionInPeriods = (
    asset: IAssetHeader,
    toCompare: IAssetHeader
  ) => {
    return (
      asset.first_quote_date <= toCompare.last_quote_date &&
      asset.last_quote_date >= toCompare.first_quote_date
    );
  };

  private isAssetComparable(
    currentAsset: IAssetHeader,
    allAssets: IAssetHeader[]
  ) {
    if (!currentAsset.has_quotes) {
      throw new Error(
        `O ativo ${currentAsset.nicename} não possui cotação e não pode ser adicionado à comparação.`
      );
    }

    const isComparable = allAssets.every((toCompare) =>
      this.hasIntersectionInPeriods(currentAsset, toCompare)
    );

    if (!isComparable) {
      throw new Error(
        `Não é possível comparar o ativo ${currentAsset.nicename} com os outros existentes na mesma comparação pois eles não coexistem no mesmo período.`
      );
    }
  }

  private maxStartDate = (periods: IAssetHeader[]): number => {
    const startDates = periods.map((assetDate) => assetDate.first_quote_date);
    return Math.max(...startDates);
  };

  private minEndDate = (periods: IAssetHeader[]): number => {
    const endDates = periods.map((assetDate) => assetDate.last_quote_date);
    return Math.min(...endDates);
  };
}
